import type { RandomSource } from './random';
import type { RollMode } from './types';

export interface DiceGroupResult {
  count: number;
  sides: number;
  results: number[];
}

export interface ExpressionRoll {
  expression: string;
  groups: DiceGroupResult[];
  modifier: number;
  total: number;
}

export function abilityModifier(score: number): number {
  return Math.floor((score - 10) / 2);
}

export function rollDie(sides: number, random: RandomSource): number {
  if (!Number.isInteger(sides) || sides < 2) {
    throw new Error('骰子面数必须是大于 1 的整数');
  }
  return Math.floor(random.next() * sides) + 1;
}

export function rollD20(mode: Exclude<RollMode, 'auto'>, random: RandomSource) {
  const first = rollDie(20, random);
  if (mode === 'normal') return { rolls: [first], kept: first };
  const second = rollDie(20, random);
  return {
    rolls: [first, second],
    kept: mode === 'advantage' ? Math.max(first, second) : Math.min(first, second),
  };
}

export function rollExpression(expression: string, random: RandomSource): ExpressionRoll {
  const normalized = expression.toLowerCase().replaceAll(' ', '');
  if (!normalized) throw new Error('请输入骰子表达式');
  const tokens = normalized.match(/[+-]?[^+-]+/g);
  if (!tokens?.length) throw new Error('骰子表达式格式无效');

  const groups: DiceGroupResult[] = [];
  let modifier = 0;
  let total = 0;

  for (const token of tokens) {
    const sign = token.startsWith('-') ? -1 : 1;
    const body = token.replace(/^[+-]/, '');
    const dice = body.match(/^(\d*)d(\d+)$/);
    if (dice) {
      const count = Number(dice[1] || 1);
      const sides = Number(dice[2]);
      if (!Number.isInteger(count) || count < 1 || count > 100) {
        throw new Error('单组骰子数量必须在 1 到 100 之间');
      }
      const results = Array.from({ length: count }, () => rollDie(sides, random) * sign);
      groups.push({ count, sides, results });
      total += results.reduce((sum, value) => sum + value, 0);
      continue;
    }
    if (!/^\d+$/.test(body)) throw new Error(`无法解析骰子项：${token}`);
    const value = Number(body) * sign;
    modifier += value;
    total += value;
  }

  return { expression: normalized, groups, modifier, total };
}
